/**
 * Auth0 client for backend-based authentication.
 *
 * This module provides authentication via the backend's auth0-fastapi integration.
 * All authentication flows go through the backend:
 * - /auth/login - Redirect to Auth0 login
 * - /auth/callback - Handle Auth0 callback (passes token in URL fragment)
 * - /auth/logout - Clear session, redirect to Auth0 logout
 * - /auth/me - Get current user from session (fallback)
 *
 * After login, the backend redirects with the access token in the URL fragment.
 * The frontend extracts it, stores in memory, and uses Authorization headers.
 */

import type { Auth0MeUser } from '../types/auth0';

// Backend URL for auth endpoints
let backendUrl = '';

/**
 * User information returned from /auth/me endpoint.
 */
export interface Auth0User {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
}

/**
 * Response from /auth/me endpoint or fragment auth data.
 */
export interface AuthMeResponse {
  authenticated: boolean;
  user?: Auth0User;
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
  /** Backend marker: the shared session ended in a deliberate sign-out (CLOUD-716). */
  explicit_logout?: boolean;
}

/**
 * User info type for storage (matches Auth0MeUser from types)
 */
export type AuthUserInfo = Auth0MeUser;

// ============================================================================
// Token Storage (in-memory for security, survives navigation within app)
// ============================================================================

let storedToken: string | null = null;
let storedRefreshToken: string | null = null;
let storedExpiresAt: number | null = null;
let storedUser: AuthUserInfo | null = null;
// CLOUD-749: a fragment-origin session's cookie is not guaranteed (true for
// the Companion's embedded WebView; not guaranteed either way for a fresh
// /auth/callback redirect until the next /auth/me confirms one).
let storedOrigin: 'fragment' | 'cookie' | null = null;

/** Buffer in ms before actual expiry to trigger a refresh (5 minutes). */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Callback invoked when a token refresh attempt fails irrecoverably.
 * The consumer (e.g. useAuth0Auth) can register a handler to log the user out.
 */
let onRefreshFailure: (() => void) | null = null;

/**
 * Register a callback that fires when token refresh fails.
 * Returns a cleanup function to unregister.
 */
export function onTokenRefreshFailure(callback: () => void): () => void {
  onRefreshFailure = callback;
  return () => {
    if (onRefreshFailure === callback) {
      onRefreshFailure = null;
    }
  };
}

// Persistence lives in the backend session cookie; this module only
// caches in-memory for the current page.

type AuthListener = () => void;
const authListeners = new Set<AuthListener>();

/**
 * Subscribe to in-memory auth state changes. Used by useAuth0Auth to
 * pick up logins that don't go through /auth/callback (i.e. ROPC).
 */
export function subscribeToAuthChanges(listener: AuthListener): () => void {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

function notifyAuthChanged(): void {
  for (const listener of authListeners) {
    try {
      listener();
    } catch (err) {
      console.error('[@nexus/supabase] auth change listener threw:', err);
    }
  }
}

// Triggers useAuth0Auth to re-resolve /auth/me — used after an out-of-band
// auth event (e.g. same-origin password login set the cookie).
export function notifySessionEstablished(): void {
  notifyAuthChanged();
}

// Drop tokens left behind in localStorage by older versions.
const LEGACY_STORAGE_KEYS = ['auth_token', 'auth_refresh_token', 'auth_expires_at', 'auth_user'];
try {
  if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
    for (const key of LEGACY_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  }
} catch {
  // localStorage may be a partial stub in some test/sandbox environments.
}

/**
 * Store authentication data in memory after login.
 *
 * `refreshToken` is always replaced (undefined clears it) so a stale
 * value from a previous login can't outlive its session.
 */
export function setStoredAuth(
  token: string,
  user: AuthUserInfo,
  refreshToken?: string,
  expiresAt?: number,
  origin: 'fragment' | 'cookie' = 'cookie'
): void {
  // Skip notify if nothing changed, or a re-notify cycle never ends (CLOUD-732).
  const newRefreshToken = refreshToken ?? null;
  const unchanged =
    storedToken === token &&
    storedRefreshToken === newRefreshToken &&
    storedExpiresAt === (expiresAt ?? storedExpiresAt) &&
    storedUser?.sub === user.sub &&
    storedUser?.email === user.email &&
    storedUser?.name === user.name &&
    storedUser?.picture === user.picture &&
    storedOrigin === origin;

  storedToken = token;
  storedUser = user;
  storedRefreshToken = newRefreshToken;
  storedOrigin = origin;
  if (typeof expiresAt === 'number') {
    storedExpiresAt = expiresAt;
  }
  if (!unchanged) notifyAuthChanged();
}

/**
 * Get the stored access token (without checking expiration).
 */
export function getStoredToken(): string | null {
  return storedToken;
}

/**
 * Get the stored token expiration timestamp.
 */
export function getStoredExpiresAt(): number | null {
  return storedExpiresAt;
}

/**
 * Check whether the stored access token is expired or about to expire.
 */
export function isTokenExpired(): boolean {
  const expiresAt = getStoredExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return Date.now() >= expiresAt - REFRESH_BUFFER_MS;
}

/**
 * Get the stored refresh token.
 */
export function getStoredRefreshToken(): string | null {
  return storedRefreshToken;
}

/**
 * Alias for getStoredRefreshToken.
 */
export function getRefreshToken(): string | null {
  return getStoredRefreshToken();
}

/**
 * Get the stored user info.
 */
export function getStoredUser(): AuthUserInfo | null {
  return storedUser;
}

/**
 * How the current session's token was obtained. `'fragment'` covers both a
 * fresh `/auth/callback` redirect and the Companion's cookie-less embedded
 * WebView — a cookie is not guaranteed either way (CLOUD-749).
 */
export function getStoredAuthOrigin(): 'fragment' | 'cookie' | null {
  return storedOrigin;
}

/**
 * Clear stored authentication data (on logout).
 */
export function clearStoredAuth(): void {
  const alreadyClear = storedToken === null && storedUser === null;
  storedToken = null;
  storedRefreshToken = null;
  storedExpiresAt = null;
  storedUser = null;
  storedOrigin = null;
  if (!alreadyClear) notifyAuthChanged();
}

// ============================================================================
// Fragment-based Authentication
// ============================================================================

/**
 * Extract auth data from URL fragment after callback redirect.
 * Returns null if no auth data in fragment.
 *
 * The backend passes auth data as: #auth=<base64_encoded_json>
 */
export function getAuthFromFragment(): AuthMeResponse | null {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash;
  if (!hash.includes('auth=')) return null;

  try {
    const authParam = hash.split('auth=')[1]?.split('&')[0];
    if (!authParam) return null;

    // Decode base64 (URL-safe variant)
    const decoded = atob(authParam.replace(/-/g, '+').replace(/_/g, '/'));
    const authData = JSON.parse(decoded);

    // Clean the URL (remove fragment) immediately for security
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

    // Calculate expires_at from the auth data (backend sends it)
    const expiresAt: number | undefined =
      authData.expires_at ?? (authData.expires_in ? Date.now() + authData.expires_in * 1000 : undefined);

    return {
      authenticated: true,
      user: authData.user,
      access_token: authData.access_token,
      refresh_token: authData.refresh_token,
      expires_at: expiresAt,
    };
  } catch (error) {
    console.error('[@nexus/supabase] Failed to parse auth fragment:', error);
    return null;
  }
}

/**
 * Initialize the Auth0 client with the backend URL.
 * This should be called once at app startup.
 *
 * @example
 * ```typescript
 * import { initAuth0 } from '@nexus/supabase';
 *
 * initAuth0({ backendUrl: 'http://localhost:8787' });
 * ```
 */
export function initAuth0(config: { backendUrl: string }): void {
  backendUrl = config.backendUrl;
}

/**
 * Check if the Auth0 client has been initialized.
 */
export function isAuth0Initialized(): boolean {
  return backendUrl !== '';
}

/**
 * Get the backend URL.
 */
export function getBackendUrl(): string {
  return backendUrl;
}

/**
 * Start the login flow by redirecting to the backend's /auth/login endpoint.
 *
 * @param connection - Optional Auth0 connection name (e.g., 'microsoft-graph')
 * @param returnTo - Optional URL to return to after login (defaults to current page)
 * @param workspace - Optional Business Cloud workspace id (workspace switch)
 * @param forceLogin - Forces a fresh Auth0 prompt instead of answering from a live session
 *
 * @example
 * ```typescript
 * // Login with default connection
 * startLogin();
 *
 * // Login with Microsoft
 * startLogin('microsoft-graph');
 * ```
 */
export function startLogin(connection?: string, returnTo?: string, workspace?: string, forceLogin?: boolean): void {
  if (!isAuth0Initialized()) {
    console.error('[@nexus/supabase] Auth0 not initialized. Call initAuth0() first.');
    return;
  }

  const params = new URLSearchParams();
  if (connection) {
    params.set('connection', connection);
  }
  if (returnTo) {
    params.set('returnTo', returnTo);
  }
  if (workspace) {
    params.set('workspace', workspace);
  }
  if (forceLogin) {
    params.set('forceLogin', '1');
  }

  const queryString = params.toString();
  const url = queryString ? `${backendUrl}/auth/login?${queryString}` : `${backendUrl}/auth/login`;

  window.location.href = url;
}

/**
 * Log out by redirecting to the backend's /auth/logout endpoint.
 *
 * @param returnTo - Optional URL to return to after logout (defaults to current origin)
 *
 * @example
 * ```typescript
 * logout();
 * ```
 */
export function logout(returnTo?: string): void {
  if (!isAuth0Initialized()) {
    console.error('[@nexus/supabase] Auth0 not initialized. Call initAuth0() first.');
    return;
  }

  const params = new URLSearchParams();
  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  const queryString = params.toString();
  const url = queryString ? `${backendUrl}/auth/logout?${queryString}` : `${backendUrl}/auth/logout`;

  window.location.href = url;
}

// Inflight request deduplication — concurrent callers share one fetch
let inflightAuthRequest: Promise<AuthMeResponse | null> | null = null;

/**
 * Get the current authenticated user from the backend session.
 *
 * `null` means indeterminate (no real answer, e.g. gateway/network failure).
 * Only a parsed response body is a confirmed authenticated/unauthenticated result.
 *
 * @example
 * ```typescript
 * const authState = await getAuthState();
 * if (authState?.authenticated) {
 *   console.log('User:', authState.user);
 *   console.log('Token:', authState.access_token);
 * }
 * ```
 */
export async function getAuthState(): Promise<AuthMeResponse | null> {
  if (!isAuth0Initialized()) {
    return null;
  }

  if (inflightAuthRequest) return inflightAuthRequest;

  inflightAuthRequest = (async () => {
    try {
      const response = await fetch(`${backendUrl}/auth/me`, {
        credentials: 'include', // Include cookies for session
      });

      if (!response.ok) {
        // 401/403 on a cold/anonymous load is a common, expected gateway
        // answer here — don't log it (trips Lighthouse's console-error audit).
        if (response.status !== 401 && response.status !== 403) {
          console.error('[@nexus/supabase] Error fetching auth state:', response.status);
        }
        return null;
      }

      const data: AuthMeResponse = await response.json();
      return data;
    } catch (error) {
      console.error('[@nexus/supabase] Error fetching auth state:', error);
      return null;
    } finally {
      inflightAuthRequest = null;
    }
  })();

  return inflightAuthRequest;
}

/**
 * Re-read the shared `.ari-os.com` session from `/auth/me` and adopt its token.
 *
 * The in-memory token is not authoritative: a sibling app can replace the
 * session cookie while this tab stays open (CLOUD-714 workspace switch). Unlike
 * getAccessToken(), this never short-circuits on the cached token.
 *
 * Fails open — an anonymous or failed answer leaves the current session alone,
 * so a flaky /auth/me cannot sign a talking user out. Returns the new token
 * when it changed, else null.
 */
export async function resyncSession(): Promise<string | null> {
  const authState = await getAuthState();
  if (!authState?.authenticated || !authState.access_token || !authState.user) {
    return null;
  }
  if (authState.access_token === storedToken) {
    return null;
  }

  setStoredAuth(
    authState.access_token,
    authState.user,
    authState.refresh_token,
    authState.expires_at ?? Date.now() + 3600000
  );
  return authState.access_token;
}

// ============================================================================
// Token Refresh
// ============================================================================

/** Guard to deduplicate concurrent refresh attempts. */
let inflightRefresh: Promise<string | null> | null = null;

/**
 * Refresh the access token. Cookie-first: the backend reads the session
 * cookie and rotates server-side. If we happen to hold a refresh token
 * in memory (ROPC), send it in the body as a fallback.
 *
 * Returns the new token, or null if refresh failed (in which case
 * onRefreshFailure fires so the UI can log the user out).
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    if (!isAuth0Initialized()) {
      console.warn('[@nexus/supabase] Auth0 not initialised, cannot refresh');
      onRefreshFailure?.();
      return null;
    }

    const ropcRefreshToken = getStoredRefreshToken();

    try {
      // A fragment-origin session's refresh token (if any) is bound to a
      // different Auth0 client than the backend's own — the exchange always
      // fails. Fail silently instead of forcing a sign-out (CLOUD-749).
      if (getStoredAuthOrigin() === 'fragment') {
        return null;
      }

      const response = await fetch(`${backendUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ropcRefreshToken ? { refresh_token: ropcRefreshToken } : {}),
      });

      if (!response.ok) {
        console.error('[@nexus/supabase] Token refresh failed:', response.status);
        onRefreshFailure?.();
        return null;
      }

      const data = await response.json();

      if (!data.access_token) {
        console.error('[@nexus/supabase] No access_token in refresh response');
        onRefreshFailure?.();
        return null;
      }

      const expiresAt =
        typeof data.expires_at === 'number'
          ? data.expires_at
          : data.expires_in
          ? Date.now() + data.expires_in * 1000
          : Date.now() + 3600000;

      const currentUser = getStoredUser();
      const refreshedUser = data.user && typeof data.user.sub === 'string' && data.user.sub ? data.user : currentUser;
      // No identity to bind the new token to — fail closed.
      if (!refreshedUser?.sub) {
        console.error('[@nexus/supabase] No user subject available for refreshed token');
        onRefreshFailure?.();
        return null;
      }
      setStoredAuth(
        data.access_token,
        refreshedUser,
        data.refresh_token, // ROPC path only
        expiresAt
      );

      console.log('[@nexus/supabase] Token refreshed successfully');
      return data.access_token as string;
    } catch (error) {
      console.error('[@nexus/supabase] Token refresh error:', error);
      onRefreshFailure?.();
      return null;
    } finally {
      // Deferred: a synchronous return above (fragment gate) would otherwise
      // have this run before the assignment below completes, re-arming the
      // stale in-flight guard.
      queueMicrotask(() => {
        inflightRefresh = null;
      });
    }
  })();

  return inflightRefresh;
}

/**
 * Get access token for backend authentication.
 *
 * If the stored token is expired (or about to expire) and a refresh token is
 * available, an automatic refresh is attempted first. When refresh fails the
 * registered `onRefreshFailure` callback fires so the UI can log the user out.
 *
 * @example
 * ```typescript
 * import { getAccessToken } from '@nexus/supabase';
 *
 * const token = await getAccessToken();
 * // Use token for authenticated API calls
 * ```
 */
export async function getAccessToken(): Promise<string> {
  // Check in-memory state first (populated from the URL fragment after a
  // backend-mediated login, or by the ROPC form). On a cold page load this
  // is null and we'll fall through to /auth/me below.
  const token = getStoredToken();

  if (token) {
    // If expired or about to expire, try to refresh silently
    if (isTokenExpired()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return refreshed;
      // If refresh failed, fall through to other methods
    } else {
      return token;
    }
  }

  // Try Auth0 backend session as fallback
  if (isAuth0Initialized()) {
    const authState = await getAuthState();
    if (authState?.authenticated && authState.access_token) {
      return authState.access_token;
    }
  }

  throw new Error('No active session - not authenticated');
}

/**
 * Bootstrap the in-memory session from a `/auth/password/login` response body.
 *
 * The proxy sets an HttpOnly session cookie AND returns the user + access token
 * in the body. Same-origin, the cookie alone is enough. But in cross-origin dev
 * (localhost → sapi-dev.ari-os.com) the cookie's `Domain=.ari-os.com` is not
 * visible to localhost, so a follow-up `/auth/me` can't see it and the SPA
 * never recognises the login. Storing the body token here (the same idea as the
 * Microsoft `#auth=` fragment flow) makes the session work either way. No-op if
 * the body carries no token/user.
 */
export function storePasswordSession(data: AuthMeResponse): void {
  if (data.access_token && data.user) {
    setStoredAuth(data.access_token, data.user, data.refresh_token, data.expires_at);
  }
}

/**
 * Ask the backend to resend the email-verification link. Same-origin so the
 * session cookie (if any) rides along; the backend always returns 200 for a
 * valid request shape (enumeration guard), so a thrown error here means a
 * transport/5xx problem worth surfacing.
 */
export async function resendVerificationEmail(email: string): Promise<void> {
  if (!isAuth0Initialized()) {
    throw new Error('Auth backend not initialized');
  }
  const response = await fetch(`${backendUrl}/auth/password/resend-verification`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error('Failed to resend verification email');
  }
}
